import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Filter, X, Check } from 'lucide-react';

// ─── Filter types ─────────────────────────────────────────────────────

export interface DirectoryFilters {
  skills: string[];
  departments: string[];
  roles: string[];
  types: string[];
  statuses: string[];
}

export const EMPTY_DIRECTORY_FILTERS: DirectoryFilters = {
  skills: [],
  departments: [],
  roles: [],
  types: [],
  statuses: [],
};

/** Count the number of active (non-empty) filter categories */
export function countActiveDirectoryFilters(filters: DirectoryFilters): number {
  let count = 0;
  if (filters.skills.length > 0) count++;
  if (filters.departments.length > 0) count++;
  if (filters.roles.length > 0) count++;
  if (filters.types.length > 0) count++;
  if (filters.statuses.length > 0) count++;
  return count;
}

/** Check if any filter is active */
export function hasActiveDirectoryFilters(filters: DirectoryFilters): boolean {
  return countActiveDirectoryFilters(filters) > 0;
}

// ─── Component ────────────────────────────────────────────────────────

interface DirectoryFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current applied filter state – lives in the parent so it persists while the sheet closes */
  filters: DirectoryFilters;
  /** Called when the user clicks Apply */
  onApply: (filters: DirectoryFilters) => void;
  /** Called when the user clicks Clear All */
  onClear: () => void;
  /** Available options derived from the loaded employee data */
  availableOptions: {
    skills: { value: string; label: string }[];
    departments: { value: string; label: string }[];
    roles: { value: string; label: string }[];
    types: { value: string; label: string }[];
    statuses: { value: string; label: string }[];
  };
}

export function DirectoryFilterSheet({
  open,
  onOpenChange,
  filters: appliedFilters,
  onApply,
  onClear,
  availableOptions,
}: DirectoryFilterSheetProps) {
  // Local draft state so changes aren't applied until the user clicks "Apply"
  const [draft, setDraft] = useState<DirectoryFilters>(EMPTY_DIRECTORY_FILTERS);

  // Sync draft from applied filters when sheet opens
  useEffect(() => {
    if (open) {
      setDraft({ ...appliedFilters });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Draft helpers ──

  const updateDraftField = useCallback(
    <K extends keyof DirectoryFilters>(key: K, value: DirectoryFilters[K]) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const clearDraft = useCallback(() => {
    setDraft(EMPTY_DIRECTORY_FILTERS);
  }, []);

  const draftActiveCount = useMemo(() => countActiveDirectoryFilters(draft), [draft]);
  const appliedActiveCount = useMemo(
    () => countActiveDirectoryFilters(appliedFilters),
    [appliedFilters]
  );

  // ── Handlers ──

  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  const handleClearAll = () => {
    clearDraft();
    onClear();
  };

  // ── Filter sections config ──

  const sections: {
    key: keyof DirectoryFilters;
    label: string;
    options: { value: string; label: string }[];
    placeholder: string;
  }[] = [
    {
      key: 'skills',
      label: 'Skills',
      options: availableOptions.skills,
      placeholder: 'Select skills...',
    },
    {
      key: 'departments',
      label: 'Department',
      options: availableOptions.departments,
      placeholder: 'Select departments...',
    },
    {
      key: 'roles',
      label: 'Role',
      options: availableOptions.roles,
      placeholder: 'Select roles...',
    },
    {
      key: 'types',
      label: 'Type',
      options: availableOptions.types,
      placeholder: 'Select types...',
    },
    {
      key: 'statuses',
      label: 'Status',
      options: availableOptions.statuses,
      placeholder: 'Select statuses...',
    },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[450px] sm:w-[450px] sm:max-w-none p-0 flex flex-col h-full gap-0"
      >
        {/* ── Fixed Header ── */}
        <SheetHeader className="px-6 pt-4 pb-3 flex-shrink-0 border-b">
          <SheetTitle className="text-base flex items-center gap-2">
            Filters
            <Badge variant="secondary" className="ml-2 flex items-center gap-1.5 px-2.5 py-0.5">
              <Filter className="h-3 w-3" />
              {appliedActiveCount} {appliedActiveCount === 1 ? 'filter' : 'filters'} active
            </Badge>
          </SheetTitle>
        </SheetHeader>

        {/* ── Scrollable Content ── */}
        <ScrollArea className="flex-1 min-h-0">
          <ScrollAreaViewport className="h-full max-h-full">
            <div className="px-6 py-4 space-y-3">
              {sections.map((section, idx) => {
                const isActive = draft[section.key].length > 0;
                return (
                  <div key={section.key}>
                    {idx > 0 && <Separator className="mb-5" />}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold">{section.label}</Label>
                        {isActive && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-muted-foreground"
                            onClick={() => updateDraftField(section.key, [])}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <MultiSelect
                        options={section.options}
                        placeholder={section.placeholder}
                        emptyMessage="No options found."
                        maxDisplay={2}
                        value={draft[section.key]}
                        onValueChange={(values) => updateDraftField(section.key, values)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* ── Fixed Footer ── */}
        <Separator className="flex-shrink-0" />
        <SheetFooter className="px-6 py-4 flex-shrink-0 flex-row items-center justify-between sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="gap-2"
            disabled={draftActiveCount === 0}
          >
            <X className="h-4 w-4" />
            Clear All
          </Button>
          <Button onClick={handleApply} className="gap-2">
            <Check className="h-4 w-4" />
            Apply Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
